import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSchool } from '../contexts/SchoolContext';
import Orders from './Orders';
import Print from './Print';
import OrderDates from './OrderDates';
import Users from './Users';
import Teachers from './Teachers';
import OrderSummary from './OrderSummary';
import OrdersManagement from './OrdersManagement';
import OrderStats from './OrderStats';
import ProfileModal from './ProfileModal';
import Help from './Help';
import SchoolSettings from './SchoolSettings';
import { schoolOrderingLink } from '../utils/schoolLinks';

// The last path segment after /admin/:schoolId/ — e.g. "orders",
// "print", "order-dates". Used to keep the same sub-page when switching
// schools via the picker (see SchoolPicker below).
function currentSubPage(pathname, schoolId) {
  const prefix = `/admin/${schoolId}/`;
  return pathname.startsWith(prefix) ? pathname.slice(prefix.length) : '';
}

function SchoolPicker() {
  const { schoolId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { schools, loading } = useSchool();

  if (loading || schools.length === 0) {
    return null;
  }

  const handleChange = (e) => {
    const subPage = currentSubPage(location.pathname, schoolId);
    navigate(`/admin/${e.target.value}/${subPage}`);
  };

  return (
    <select
      value={schoolId}
      onChange={handleChange}
      style={{
        padding: '0.4rem 0.6rem',
        border: '1px solid #ced4da',
        borderRadius: '4px',
        fontSize: '0.9rem',
        backgroundColor: 'white',
        cursor: 'pointer'
      }}
    >
      {schools.map(school => (
        <option key={school.id} value={school.id}>{school.name}</option>
      ))}
    </select>
  );
}

const NAV_ITEMS = [
  { suffix: '/orders', label: '📋 View Orders' },
  { suffix: '/print', label: '🖨️ Print Preview' },
  { suffix: '/order-summary', label: '📊 Order Summary' },
  { suffix: '/order-stats', label: '📈 Order Stats' },
  { suffix: '/orders-management', label: '🗂️ Orders Management' },
  { suffix: '/order-dates', label: '📅 Order Dates' },
  { suffix: '/teachers', label: '🍎 Teachers' },
  { suffix: '/users', label: '👥 Users' },
  { suffix: '/settings', label: '⚙️ School Settings' },
  { suffix: '/help', label: '❓ Help' }
];

// Dropdown replacing the standalone Profile button — same action, plus a
// "Copy Ordering Link" action that used to live in its own banner (first
// in the sidebar, squished by its fixed 220px width; then as a page
// footer, which read as competing for attention with the actual page
// content). Help is deliberately not in here — it's a normal nav
// destination (see NAV_ITEMS/Sidebar), not an account-level action.
// Logout also stays out — it's its own standalone button in TopBar,
// same as before, rather than one more click away inside the dropdown.
//
// Closes on: selecting an item, clicking outside, or Escape — standard
// dropdown-menu behavior.
function AccountMenu({ onOpenProfile }) {
  const { schoolId } = useParams();
  const { schools } = useSchool();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef(null);
  const link = schoolOrderingLink(schoolId, schools.map(s => s.id));

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Clipboard API can fail (permissions, non-secure context, older
      // browsers) — fall back to a prompt so the link is still copyable
      // by hand rather than just silently doing nothing.
      window.prompt('Copy your ordering link:', link);
      setOpen(false);
    }
  };

  return (
    <div className="account-menu" ref={menuRef}>
      <button
        onClick={() => setOpen(prev => !prev)}
        className="account-menu-trigger"
        aria-haspopup="true"
        aria-expanded={open}
      >
        ☰ Menu
      </button>
      {open && (
        <div className="account-menu-dropdown">
          <button
            className="account-menu-item"
            onClick={() => { setOpen(false); onOpenProfile(); }}
          >
            👤 Profile
          </button>
          <div className="account-menu-divider" />
          <div className="account-menu-link-item">
            <span className="account-menu-link-label">Your ordering link</span>
            <span className="account-menu-link-value">{link}</span>
            <div className="account-menu-link-actions">
              <button className="account-menu-copy-button" onClick={handleCopyLink}>
                {copied ? '✅ Copied!' : '🔗 Copy Link'}
              </button>
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="account-menu-copy-button"
                onClick={() => setOpen(false)}
              >
                ↗ Open Link
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Top bar: title, school picker, welcome, account menu, logout. Page
// navigation itself lives in the left-hand Sidebar below — keeping this
// bar to just account-level controls is what keeps it from ever getting
// crowded.
function TopBar({ onToggleSidebar }) {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [showProfile, setShowProfile] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <>
      <header className="header no-print">
        <div className="admin-topbar">
          <div className="admin-topbar-left">
            <button
              className="sidebar-toggle"
              onClick={onToggleSidebar}
              aria-label="Toggle navigation menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
            <h1>🥪 Admin Dashboard</h1>
          </div>

          <div className="admin-topbar-right">
            <SchoolPicker />
            <span className="nav-welcome" style={{ color: '#ccd6e0', fontSize: '0.9rem' }}>
              Welcome, {user?.username}
            </span>
            <AccountMenu onOpenProfile={() => setShowProfile(true)} />
            <button
              onClick={handleLogout}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {showProfile && (
        <ProfileModal onClose={() => setShowProfile(false)} />
      )}
    </>
  );
}

// Left-hand page navigation. A fixed column on wide screens; on narrow
// screens it's an off-canvas drawer toggled by TopBar's hamburger button.
function Sidebar({ open, onClose }) {
  const { schoolId } = useParams();
  const location = useLocation();
  const { isFullAdmin } = useAuth();

  const base = `/admin/${schoolId}`;
  const isActive = (suffix) => location.pathname === `${base}${suffix}`;

  return (
    <>
      {open && <div className="sidebar-backdrop" onClick={onClose} />}
      <nav className={`sidebar ${open ? 'open' : ''}`}>
        {NAV_ITEMS.map(item => (
          <Link
            key={item.suffix}
            to={`${base}${item.suffix}`}
            className={`sidebar-link ${isActive(item.suffix) ? 'active' : ''}`}
            onClick={onClose}
          >
            {item.label}
          </Link>
        ))}
        {isFullAdmin && (
          <>
            <div className="sidebar-divider" />
            <Link
              to="/admin/main/schools"
              className="sidebar-link"
              onClick={onClose}
            >
              🏢 Admin Area
            </Link>
          </>
        )}
      </nav>
    </>
  );
}

function AdminDashboard() {
  const { schoolId } = useParams();
  const location = useLocation();
  const { schools, loading } = useSchool();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const base = `/admin/${schoolId}`;
  const isPrintPage = location.pathname === `${base}/print`;
  const isOrderSummaryPage = location.pathname === `${base}/order-summary`;
  const isOrderStatsPage = location.pathname === `${base}/order-stats`;
  const isOrdersManagementPage = location.pathname === `${base}/orders-management`;
  const isOrderDatesPage = location.pathname === `${base}/order-dates`;
  const isUsersPage = location.pathname === `${base}/users`;
  const isTeachersPage = location.pathname === `${base}/teachers`;
  const isSettingsPage = location.pathname === `${base}/settings`;
  const isHelpPage = location.pathname === `${base}/help`;

  const renderPage = () => {
    if (isPrintPage) {
      return <Print />;
    } else if (isOrderSummaryPage) {
      return <OrderSummary />;
    } else if (isOrderStatsPage) {
      return <OrderStats />;
    } else if (isOrdersManagementPage) {
      return <OrdersManagement />;
    } else if (isOrderDatesPage) {
      return <OrderDates />;
    } else if (isUsersPage) {
      return <Users />;
    } else if (isTeachersPage) {
      return <Teachers />;
    } else if (isSettingsPage) {
      return <SchoolSettings />;
    } else if (isHelpPage) {
      return <Help />;
    } else {
      return <Orders />;
    }
  };

  // Once the school list has loaded, reject an unknown :schoolId (typo,
  // stale bookmark, a school removed from schools.yaml) rather than letting
  // every admin API call 404 silently underneath a broken page.
  if (!loading && schools.length > 0 && !schools.some(s => s.id === schoolId)) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <h2>Unknown school</h2>
        <p style={{ color: '#666' }}>"{schoolId}" doesn't match any configured school.</p>
        <Link to="/admin">← Choose a school</Link>
      </div>
    );
  }

  return (
    <div>
      <TopBar onToggleSidebar={() => setSidebarOpen(prev => !prev)} />

      <div className="admin-body">
        <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {/* Keying on schoolId remounts whichever admin page is showing when
            the school picker changes (or the user edits the URL directly),
            so it refetches fresh data for the newly-selected school instead
            of showing stale data from the previous one. */}
        <div className="admin-content" key={schoolId}>
          {renderPage()}
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
